import type { CSSProperties, ReactNode } from 'react'
import { IconButton } from './actions.js'
import { cx } from './cx.js'
import { Icons } from './icons.js'
import { Monogram } from './identity.js'

// ── popups (FR-MAP-15) ────────────────────────────────────────────────────
/**
 * A large popup over the default view: fills a phone, keeps phone width on anything wider, closes
 * when the scrim is tapped. The screen inside supplies its own header with a close X (CloseButton).
 */
export function Popup({
  children,
  onClose,
  label,
  wide,
}: {
  children: ReactNode
  onClose: () => void
  label?: string
  /** the whole width of the screen, for reading (Learn More, the Safety Center); the text keeps a column */
  wide?: boolean
}) {
  return (
    <div className={cx('fm-popup', wide && 'fm-popup--wide')}>
      <button type="button" className="fm-popup__scrim" aria-label="Close" onClick={onClose} />
      <section className="fm-popup__panel" aria-label={label} role="dialog" aria-modal="true">
        {children}
      </section>
    </div>
  )
}
export function CloseButton({ onClick, label = 'Close' }: { onClick: () => void; label?: string }) {
  return (
    <IconButton bare aria-label={label} onClick={onClick}>
      {Icons.close()}
    </IconButton>
  )
}
/** The brand mark and wordmark, for the band above the search bar. */
export function Wordmark({ size = 28, mark }: { size?: number; mark?: number }) {
  return (
    <span className="fm-wordmark">
      {/* The radar's rings need room: the logo runs larger than the type it sits beside. `mark` sets
          the logo's own size, e.g. to match a round icon across from it. */}
      <Monogram size={mark ?? Math.round(size * 1.3)} />
      <span className="fm-display" style={{ fontSize: Math.round(size * 0.42) }}>
        Feral Mode Live
      </span>
    </span>
  )
}
/** An anchored menu: a transparent scrim closes it, rows are buttons or links the caller supplies. */
export function Menu({
  children,
  onClose,
  style,
}: {
  children: ReactNode
  onClose: () => void
  style?: CSSProperties
}) {
  return (
    <>
      <button type="button" className="fm-menu__scrim" aria-label="Close menu" onClick={onClose} />
      <div className="fm-menu" role="menu" style={style}>
        {children}
      </div>
    </>
  )
}
